import { DRAWABLE_HEIGHT_METERS, DRAWABLE_WIDTH_METERS } from './floorPlanDesignerConfig.js';
import {
  DEFAULT_WALL_TEXTURE_MATERIAL,
  DEFAULT_WALL_OUTSIDE_TEXTURE_MATERIAL,
  getWallMaterialConfig,
  normalizeMaterialConfig,
} from './floorPlanMaterialConfig.js';

/** @typedef {[number, number]} Point */

/**
 * @typedef {{
 *   id: string;
 *   x: number;
 *   y: number;
 * }} DesignerPoint
 */

/**
 * @typedef {{
 *   id: string;
 *   startPointId: string;
 *   endPointId: string;
 *   thicknessMeters: number;
 *   color: string;
 *   material?: import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig;
 *   insideMaterial?: import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig;
 *   outsideMaterial?: import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig;
 *   visibleIn3D?: boolean;
 * }} DesignerWall
 */

/**
 * @typedef {{
 *   id: string;
 *   type: 'door' | 'window';
 *   wallId: string;
 *   centerRatio: number;
 *   widthMeters: number;
 *   heightMeters: number;
 *   sillHeightMeters: number;
 *   thicknessMeters?: number;
 *   hingeSide?: 'left' | 'right';
 *   swingSide?: 'inside' | 'outside';
 *   swingDirection?: 'left' | 'right';
 *   openAngle?: number;
 *   doorState?: 'open' | 'closed' | 'frame-only';
 * }} DesignerOpening
 */

/**
 * @typedef {{
 *   thicknessMeters: number;
 *   thicknessDirection: 'inside' | 'outside';
 * }} DesignerWallSettings
 */

/**
 * @typedef {{
 *   canvasWidth: number;
 *   canvasHeight: number;
 *   points: DesignerPoint[];
 *   walls: DesignerWall[];
 *   openings: DesignerOpening[];
 *   objects: DesignerObject[];
 *   wallSettings: DesignerWallSettings;
 *   scale: { pixelsPerMeter: number; metersPerPixel: number; source: string };
 *   camera: { position: Point; direction: Point; fov?: number; heightMeters?: number } | null;
 *   activeChainPointIds: string[];
 *   draftPreviewPoint: Point | null;
 *   roomsMeta: Record<string, { name: string; floorColor?: string; floorMaterial?: import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig; visibleIn3D?: boolean }>;
 *   backgroundReferenceImage?: import('./floorPlanBackgroundImageState.js').BackgroundReferenceImage | null;
 * }} DesignerState
 */

/**
 * @typedef {{
 *   id: string;
 *   type: 'door';
 *   wallId: string;
 *   positionOnWall: number;
 *   width: number;
 *   height: number;
 *   thickness: number;
 *   thickness2D: number;
 *   swingDirection: 'left' | 'right';
 *   openAngle: number;
 * }} DesignerDoorObject
 */

/**
 * @typedef {{
 *   id: string;
 *   type: 'window';
 *   wallId: string;
 *   positionOnWall: number;
 *   width: number;
 *   height: number;
 *   sillHeight: number;
 *   thickness: number;
 *   thickness2D: number;
 *   frameDepth: number;
 * }} DesignerWindowObject
 */

/**
 * @typedef {{
 *   id: string;
 *   type: 'furniture';
 *   category: 'sofa' | 'bed' | 'table' | 'chair' | 'cabinet';
 *   x: number;
 *   z: number;
 *   rotationY: number;
 *   width: number;
 *   depth: number;
 *   height: number;
 *   modelUrl?: string;
 * }} DesignerFurnitureObject
 */

/** @typedef {DesignerDoorObject | DesignerWindowObject | DesignerFurnitureObject} DesignerObject */

export const DEFAULT_WALL_THICKNESS_M = 0.1;
export const DEFAULT_WALL_THICKNESS_DIRECTION = 'outside';
export const MIN_WALL_LENGTH_METERS = 0.1;
export const POINT_ON_WALL_TOLERANCE_M = 0.08;
export const MIN_SPLIT_WALL_LENGTH_M = MIN_WALL_LENGTH_METERS;
/** Min distance from wall endpoint to insert a split point (image px). */
export const SPLIT_POINT_EPS_PX = 4;
export const DEFAULT_WALL_COLOR = '#3F3F46';
export const DEFAULT_PIXELS_PER_METER = 100;
export const DEFAULT_CANVAS_SIZE = DRAWABLE_WIDTH_METERS * DEFAULT_PIXELS_PER_METER;
export const DEFAULT_CANVAS_HEIGHT = DRAWABLE_HEIGHT_METERS * DEFAULT_PIXELS_PER_METER;
export const DEFAULT_DOOR_THICKNESS_M = 0.2;
export const DEFAULT_WINDOW_THICKNESS_M = 0.2;

export const WALL_COLOR_PRESETS = ['#3F3F46', '#4b5563', '#78716c', '#57534e', '#334155'];

export const DEFAULT_DOOR = {
  widthMeters: 0.9,
  heightMeters: 2.1,
  sillHeightMeters: 0,
  thicknessMeters: DEFAULT_DOOR_THICKNESS_M,
  hingeSide: 'left',
  swingSide: 'inside',
  swingDirection: 'left',
  doorState: 'closed',
  openAngle: 0,
};

export const DEFAULT_WINDOW = {
  widthMeters: 1.2,
  heightMeters: 1.2,
  sillHeightMeters: 0.9,
  thicknessMeters: DEFAULT_WINDOW_THICKNESS_M,
  frameDepthMeters: 0.08,
};

export const DEFAULT_FURNITURE_DIMENSIONS = {
  sofa: { width: 2.0, depth: 0.9, height: 0.8 },
  bed: { width: 2.0, depth: 1.6, height: 0.55 },
  table: { width: 1.4, depth: 0.8, height: 0.75 },
  chair: { width: 0.5, depth: 0.55, height: 0.9 },
  cabinet: { width: 1.2, depth: 0.45, height: 1.8 },
};

let idCounter = 0;

export function nextDesignerId(prefix) {
  idCounter += 1;
  return `${prefix}_${idCounter}`;
}

function nextUniqueDesignerId(prefix, existingIds) {
  let id = nextDesignerId(prefix);
  while (existingIds.has(id)) {
    id = nextDesignerId(prefix);
  }
  return id;
}

export function resetDesignerIdCounter() {
  idCounter = 0;
}

/**
 * @returns {DesignerState}
 */
export function createEmptyDesignerState() {
  resetDesignerIdCounter();
  return {
    canvasWidth: DEFAULT_CANVAS_SIZE,
    canvasHeight: DEFAULT_CANVAS_SIZE,
    points: [],
    walls: [],
    openings: [],
    objects: [],
    wallSettings: {
      thicknessMeters: DEFAULT_WALL_THICKNESS_M,
      thicknessDirection: DEFAULT_WALL_THICKNESS_DIRECTION,
    },
    scale: {
      pixelsPerMeter: DEFAULT_PIXELS_PER_METER,
      metersPerPixel: 1 / DEFAULT_PIXELS_PER_METER,
      source: 'designer_default',
    },
    camera: null,
    activeChainPointIds: [],
    draftPreviewPoint: null,
    roomsMeta: {},
    backgroundReferenceImage: null,
  };
}

/**
 * @param {DesignerState} state
 * @param {string} id
 * @returns {DesignerPoint | undefined}
 */
export function getPointById(state, id) {
  return state.points.find((p) => p.id === id);
}

/**
 * @param {DesignerState} state
 * @returns {Array<{ id: string; start: Point; end: Point; thicknessMeters: number; color: string; startPointId: string; endPointId: string }>}
 */
export function getWallSegmentsResolved(state) {
  return state.walls
    .map((wall) => {
      const startPt = getPointById(state, wall.startPointId);
      const endPt = getPointById(state, wall.endPointId);
      if (!startPt || !endPt) return null;
      return {
        id: wall.id,
        start: [startPt.x, startPt.y],
        end: [endPt.x, endPt.y],
        startPointId: wall.startPointId,
        endPointId: wall.endPointId,
        thicknessMeters: wall.thicknessMeters,
        color: wall.color,
        material: getWallMaterialConfig(wall),
        insideMaterial: getWallMaterialConfig(wall, 'inside'),
        outsideMaterial: getWallMaterialConfig(wall, 'outside'),
        visibleIn3D: wall.visibleIn3D ?? true,
      };
    })
    .filter(Boolean);
}

/**
 * @param {DesignerState} state
 * @param {Point} coords
 * @param {number} [tolerancePx]
 * @returns {DesignerPoint | null}
 */
export function findPointNear(state, coords, tolerancePx = 8) {
  let best = null;
  let bestDist = Infinity;
  for (const pt of state.points) {
    const dist = Math.hypot(pt.x - coords[0], pt.y - coords[1]);
    if (dist < bestDist) {
      bestDist = dist;
      best = pt;
    }
  }
  return best && bestDist <= tolerancePx ? best : null;
}

/**
 * @param {DesignerState} state
 * @param {Point} coords
 * @returns {{ state: DesignerState; pointId: string }}
 */
export function addPoint(state, coords) {
  const existingIds = new Set(state.points.map((point) => point.id));
  const id = nextUniqueDesignerId('p', existingIds);
  const point = { id, x: coords[0], y: coords[1] };
  return {
    state: { ...state, points: [...state.points, point] },
    pointId: id,
  };
}

/**
 * @param {DesignerState} state
 * @param {string} startPointId
 * @param {string} endPointId
 * @returns {DesignerState}
 */
export function addWall(state, startPointId, endPointId) {
  if (startPointId === endPointId) return state;
  const exists = state.walls.some(
    (w) =>
      (w.startPointId === startPointId && w.endPointId === endPointId) ||
      (w.startPointId === endPointId && w.endPointId === startPointId),
  );
  if (exists) return state;

  const wall = {
    id: nextUniqueDesignerId('w', new Set(state.walls.map((item) => item.id))),
    startPointId,
    endPointId,
    thicknessMeters: DEFAULT_WALL_THICKNESS_M,
    color: DEFAULT_WALL_COLOR,
    material: { ...DEFAULT_WALL_TEXTURE_MATERIAL },
    insideMaterial: { ...DEFAULT_WALL_TEXTURE_MATERIAL },
    outsideMaterial: { ...DEFAULT_WALL_OUTSIDE_TEXTURE_MATERIAL },
    visibleIn3D: true,
  };
  return { ...state, walls: [...state.walls, wall] };
}

/**
 * @param {DesignerState} state
 * @param {string} pointId
 * @param {Point} coords
 * @returns {DesignerState}
 */
export function movePoint(state, pointId, coords) {
  return {
    ...state,
    points: state.points.map((p) =>
      p.id === pointId ? { ...p, x: coords[0], y: coords[1] } : p,
    ),
  };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {[number, number]} delta
 * @returns {DesignerState}
 */
export function moveWall(state, wallId, delta) {
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) return state;

  const startPt = getPointById(state, wall.startPointId);
  const endPt = getPointById(state, wall.endPointId);
  if (!startPt || !endPt) return state;

  let next = movePoint(state, wall.startPointId, [startPt.x + delta[0], startPt.y + delta[1]]);
  next = movePoint(next, wall.endPointId, [endPt.x + delta[0], endPt.y + delta[1]]);
  return next;
}

function projectPointOntoWallRatio(startPt, endPt, point) {
  const dx = endPt.x - startPt.x;
  const dy = endPt.y - startPt.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) {
    return {
      t: 0,
      projected: { x: startPt.x, y: startPt.y },
      dist: Math.hypot(point.x - startPt.x, point.y - startPt.y),
    };
  }

  const rawT = ((point.x - startPt.x) * dx + (point.y - startPt.y) * dy) / lenSq;
  const t = Math.max(0, Math.min(1, rawT));
  const projected = { x: startPt.x + dx * t, y: startPt.y + dy * t };
  return {
    t,
    projected,
    dist: Math.hypot(point.x - projected.x, point.y - projected.y),
  };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {Point} splitPoint
 * @param {{ skipCleanup?: boolean }} [options]
 * @returns {{ state: DesignerState; pointId: string; split: boolean; newWallIds: string[] }}
 */
export function splitWallAtPoint(state, wallId, splitPoint, options = {}) {
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) {
    return { state, pointId: '', split: false, newWallIds: [] };
  }

  const startPt = getPointById(state, wall.startPointId);
  const endPt = getPointById(state, wall.endPointId);
  if (!startPt || !endPt) {
    return { state, pointId: '', split: false, newWallIds: [] };
  }

  const dx = endPt.x - startPt.x;
  const dy = endPt.y - startPt.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) {
    return { state, pointId: wall.startPointId, split: false, newWallIds: [wallId] };
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((splitPoint[0] - startPt.x) * dx + (splitPoint[1] - startPt.y) * dy) / lenSq,
    ),
  );

  const coords = [startPt.x + dx * t, startPt.y + dy * t];
  const distStart = Math.hypot(coords[0] - startPt.x, coords[1] - startPt.y);
  const distEnd = Math.hypot(coords[0] - endPt.x, coords[1] - endPt.y);

  if (distStart < SPLIT_POINT_EPS_PX || t <= 0.01) {
    return { state, pointId: wall.startPointId, split: false, newWallIds: [wallId] };
  }
  if (distEnd < SPLIT_POINT_EPS_PX || t >= 0.99) {
    return { state, pointId: wall.endPointId, split: false, newWallIds: [wallId] };
  }

  const { state: withPoint, pointId } = addPoint(state, coords);
  const existingWallIds = new Set(state.walls.map((item) => item.id));
  const w1Id = nextUniqueDesignerId('w', existingWallIds);
  existingWallIds.add(w1Id);
  const w2Id = nextUniqueDesignerId('w', existingWallIds);
  const w1 = {
    id: w1Id,
    startPointId: wall.startPointId,
    endPointId: pointId,
    thicknessMeters: wall.thicknessMeters,
    color: wall.color,
    visibleIn3D: wall.visibleIn3D ?? true,
    ...(wall.material ? { material: wall.material } : {}),
    ...(wall.insideMaterial ? { insideMaterial: wall.insideMaterial } : {}),
    ...(wall.outsideMaterial ? { outsideMaterial: wall.outsideMaterial } : {}),
  };
  const w2 = {
    id: w2Id,
    startPointId: pointId,
    endPointId: wall.endPointId,
    thicknessMeters: wall.thicknessMeters,
    color: wall.color,
    visibleIn3D: wall.visibleIn3D ?? true,
    ...(wall.material ? { material: wall.material } : {}),
    ...(wall.insideMaterial ? { insideMaterial: wall.insideMaterial } : {}),
    ...(wall.outsideMaterial ? { outsideMaterial: wall.outsideMaterial } : {}),
  };

  const nextOpenings = withPoint.openings.map((o) => {
    if (o.wallId !== wallId) return o;
    if (o.centerRatio <= t) {
      return { ...o, wallId: w1Id, centerRatio: o.centerRatio / t };
    }
    return { ...o, wallId: w2Id, centerRatio: (o.centerRatio - t) / (1 - t) };
  });
  const nextObjects = (withPoint.objects || []).map((object) => {
    if ((object.type !== 'door' && object.type !== 'window') || object.wallId !== wallId) {
      return object;
    }
    if (object.positionOnWall <= t) {
      return { ...object, wallId: w1Id, positionOnWall: object.positionOnWall / t };
    }
    return {
      ...object,
      wallId: w2Id,
      positionOnWall: (object.positionOnWall - t) / (1 - t),
    };
  });

  const nextState = {
    ...withPoint,
    walls: [...withPoint.walls.filter((w) => w.id !== wallId), w1, w2],
    openings: nextOpenings,
    objects: nextObjects,
  };

  return {
    state: options.skipCleanup ? nextState : cleanupFloorPlanState(nextState),
    pointId,
    split: true,
    newWallIds: [w1Id, w2Id],
  };
}

/**
 * Split every non-incident wall that already contains an existing point.
 * @param {DesignerState} state
 * @param {string} pointId
 * @param {{ toleranceMeters?: number; minSegmentMeters?: number; excludeWallIds?: string[] }} [options]
 * @returns {{ state: DesignerState; splitWallIds: string[]; newWallIds: string[] }}
 */
export function splitWallsAtPointIfOnSegment(state, pointId, options = {}) {
  const point = getPointById(state, pointId);
  if (!point) return { state, splitWallIds: [], newWallIds: [] };

  const ppm = state.scale.pixelsPerMeter || DEFAULT_PIXELS_PER_METER;
  const tolerancePx = (options.toleranceMeters ?? POINT_ON_WALL_TOLERANCE_M) * ppm;
  const minSegmentPx = Math.max(
    (options.minSegmentMeters ?? MIN_SPLIT_WALL_LENGTH_M) * ppm,
    SPLIT_POINT_EPS_PX,
  );
  const excludedWalls = new Set(options.excludeWallIds ?? []);
  let nextWalls = [...state.walls];
  let nextPoints = [...state.points];
  let nextOpenings = [...state.openings];
  let nextObjects = [...(state.objects || [])];
  let splitPoint = { ...point };
  const splitWallIds = [];
  const newWallIds = [];
  const existingWallIds = new Set(nextWalls.map((wall) => wall.id));

  for (const wall of state.walls) {
    if (excludedWalls.has(wall.id)) continue;
    if (wall.startPointId === pointId || wall.endPointId === pointId) continue;

    const startPt = getPointById(state, wall.startPointId);
    const endPt = getPointById(state, wall.endPointId);
    if (!startPt || !endPt) continue;

    const { t, projected, dist } = projectPointOntoWallRatio(startPt, endPt, splitPoint);
    if (dist > tolerancePx || t <= 0 || t >= 1) continue;

    const distStart = Math.hypot(projected.x - startPt.x, projected.y - startPt.y);
    const distEnd = Math.hypot(projected.x - endPt.x, projected.y - endPt.y);
    if (distStart < minSegmentPx || distEnd < minSegmentPx) continue;

    splitPoint = { ...splitPoint, x: projected.x, y: projected.y };
    nextPoints = nextPoints.map((item) => (
      item.id === pointId ? { ...item, x: projected.x, y: projected.y } : item
    ));

    const w1Id = nextUniqueDesignerId('w', existingWallIds);
    existingWallIds.add(w1Id);
    const w2Id = nextUniqueDesignerId('w', existingWallIds);
    existingWallIds.add(w2Id);

    const w1 = {
      ...wall,
      id: w1Id,
      startPointId: wall.startPointId,
      endPointId: pointId,
    };
    const w2 = {
      ...wall,
      id: w2Id,
      startPointId: pointId,
      endPointId: wall.endPointId,
    };

    nextWalls = [...nextWalls.filter((item) => item.id !== wall.id), w1, w2];
    const remappedOpenings = [];
    for (const opening of nextOpenings) {
      if (opening.wallId !== wall.id) {
        remappedOpenings.push(opening);
      } else if (opening.centerRatio <= t) {
        remappedOpenings.push({ ...opening, wallId: w1Id, centerRatio: opening.centerRatio / t });
      } else {
        remappedOpenings.push({
          ...opening,
          wallId: w2Id,
          centerRatio: (opening.centerRatio - t) / (1 - t),
        });
      }
    }
    nextOpenings = remappedOpenings;

    const remappedObjects = [];
    for (const object of nextObjects) {
      if ((object.type !== 'door' && object.type !== 'window') || object.wallId !== wall.id) {
        remappedObjects.push(object);
      } else if (object.positionOnWall <= t) {
        remappedObjects.push({
          ...object,
          wallId: w1Id,
          positionOnWall: object.positionOnWall / t,
        });
      } else {
        remappedObjects.push({
          ...object,
          wallId: w2Id,
          positionOnWall: (object.positionOnWall - t) / (1 - t),
        });
      }
    }
    nextObjects = remappedObjects;
    splitWallIds.push(wall.id);
    newWallIds.push(w1Id, w2Id);
  }

  if (!splitWallIds.length) {
    return { state, splitWallIds: [], newWallIds: [] };
  }

  return {
    state: {
      ...state,
      points: nextPoints,
      walls: nextWalls,
      openings: nextOpenings,
      objects: nextObjects,
    },
    splitWallIds,
    newWallIds,
  };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {number} centerRatio
 * @returns {{ state: DesignerState; newWallIds: string[]; pointId?: string }}
 */
export function insertPointOnWall(state, wallId, centerRatio = 0.5) {
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) return { state, newWallIds: [] };

  const startPt = getPointById(state, wall.startPointId);
  const endPt = getPointById(state, wall.endPointId);
  if (!startPt || !endPt) return { state, newWallIds: [] };

  const t = Math.max(0.01, Math.min(0.99, centerRatio));
  const coords = [
    startPt.x + (endPt.x - startPt.x) * t,
    startPt.y + (endPt.y - startPt.y) * t,
  ];

  const result = splitWallAtPoint(state, wallId, coords);
  return {
    state: result.state,
    newWallIds: result.newWallIds,
    pointId: result.pointId,
  };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @returns {DesignerState}
 */
export function removeWall(state, wallId) {
  return cleanupFloorPlanState({
    ...state,
    walls: state.walls.filter((w) => w.id !== wallId),
    openings: state.openings.filter((o) => o.wallId !== wallId),
    objects: (state.objects || []).filter(
      (object) => object.type === 'furniture' || object.wallId !== wallId,
    ),
  });
}

/**
 * @param {DesignerState} state
 * @param {DesignerWall} wall
 * @returns {number}
 */
export function wallLengthMeters(state, wall) {
  const start = getPointById(state, wall.startPointId);
  const end = getPointById(state, wall.endPointId);
  if (!start || !end) return 0;
  const lenPx = Math.hypot(end.x - start.x, end.y - start.y);
  return lenPx * state.scale.metersPerPixel;
}

/**
 * @param {DesignerState} state
 * @param {string} startPointId
 * @param {string} endPointId
 * @returns {boolean}
 */
export function isWallSegmentLongEnough(state, startPointId, endPointId) {
  const start = getPointById(state, startPointId);
  const end = getPointById(state, endPointId);
  if (!start || !end) return false;
  const lenPx = Math.hypot(end.x - start.x, end.y - start.y);
  const minLenPx = MIN_WALL_LENGTH_METERS * state.scale.pixelsPerMeter;
  return lenPx >= minLenPx;
}

/**
 * @param {DesignerState} state
 * @param {{ keepChainPoints?: boolean }} [options]
 * @returns {DesignerState}
 */
export function cleanupFloorPlanState(state, options = {}) {
  const { keepChainPoints = false } = options;
  const ppm = state.scale.pixelsPerMeter;
  const minLenPx = MIN_WALL_LENGTH_METERS * ppm;

  let walls = state.walls.filter((wall) => {
    const start = getPointById(state, wall.startPointId);
    const end = getPointById(state, wall.endPointId);
    if (!start || !end) return false;
    const lenPx = Math.hypot(end.x - start.x, end.y - start.y);
    return lenPx >= minLenPx;
  });

  const wallIds = new Set(walls.map((w) => w.id));
  const openings = state.openings.filter((o) => wallIds.has(o.wallId));
  const objects = (state.objects || []).filter(
    (object) => object.type === 'furniture' || wallIds.has(object.wallId),
  );

  const usedPointIds = new Set();
  walls.forEach((w) => {
    usedPointIds.add(w.startPointId);
    usedPointIds.add(w.endPointId);
  });

  if (keepChainPoints) {
    state.activeChainPointIds.forEach((id) => usedPointIds.add(id));
  }

  const points = state.points.filter((p) => usedPointIds.has(p.id));
  const pointIdSet = new Set(points.map((p) => p.id));

  walls = walls.filter(
    (w) => pointIdSet.has(w.startPointId) && pointIdSet.has(w.endPointId),
  );

  const activeChainPointIds = keepChainPoints
    ? state.activeChainPointIds.filter((id) => pointIdSet.has(id))
    : [];

  return {
    ...state,
    walls,
    points,
    openings,
    objects,
    activeChainPointIds,
    draftPreviewPoint: keepChainPoints ? state.draftPreviewPoint : null,
  };
}

/**
 * @param {DesignerState} state
 * @param {string} pointId
 * @returns {DesignerState}
 */
export function removePoint(state, pointId) {
  const connectedWalls = state.walls.filter(
    (w) => w.startPointId === pointId || w.endPointId === pointId,
  );
  let next = state;
  connectedWalls.forEach((w) => {
    next = removeWall(next, w.id);
  });
  return {
    ...next,
    points: next.points.filter((p) => p.id !== pointId),
    activeChainPointIds: next.activeChainPointIds.filter((id) => id !== pointId),
  };
}

function pointLineDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return Infinity;
  return Math.abs((dy * point.x) - (dx * point.y) + (end.x * start.y) - (end.y * start.x)) / len;
}

function areOppositeCollinearThroughPoint(center, a, b, tolerancePx = 1) {
  const ax = a.x - center.x;
  const ay = a.y - center.y;
  const bx = b.x - center.x;
  const by = b.y - center.y;
  const lenA = Math.hypot(ax, ay);
  const lenB = Math.hypot(bx, by);
  if (lenA < 1e-6 || lenB < 1e-6) return false;
  const dot = (ax * bx) + (ay * by);
  if (dot >= 0) return false;
  return pointLineDistance(center, a, b) <= tolerancePx;
}

function pointOnWallAtRatio(start, end, ratio) {
  return {
    x: start.x + (end.x - start.x) * ratio,
    y: start.y + (end.y - start.y) * ratio,
  };
}

function projectRatioOnWall(start, end, point) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) return 0.5;
  return Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lenSq));
}

/**
 * Delete a point. If the point only split a straight wall, reconnect the two
 * collinear wall pieces into a single wall so imported/editor topology stays editable.
 * @param {DesignerState} state
 * @param {string} pointId
 * @param {{ collinearTolerancePx?: number }} [options]
 * @returns {DesignerState}
 */
export function removePointAndHealWalls(state, pointId, options = {}) {
  const point = getPointById(state, pointId);
  if (!point) return state;

  const connectedWalls = state.walls.filter(
    (wall) => wall.startPointId === pointId || wall.endPointId === pointId,
  );
  if (connectedWalls.length === 0) {
    return {
      ...state,
      points: state.points.filter((item) => item.id !== pointId),
      activeChainPointIds: state.activeChainPointIds.filter((id) => id !== pointId),
    };
  }

  const connectedWallIds = new Set(connectedWalls.map((wall) => wall.id));
  const pointMap = new Map(state.points.map((item) => [item.id, item]));
  const candidates = connectedWalls
    .map((wall) => {
      const otherPointId = wall.startPointId === pointId ? wall.endPointId : wall.startPointId;
      const otherPoint = pointMap.get(otherPointId);
      return otherPoint ? { wall, otherPointId, otherPoint } : null;
    })
    .filter(Boolean);

  const usedWallIds = new Set();
  const healedWalls = [];
  const openingWallMap = new Map();
  const existingWallIds = new Set(state.walls.map((wall) => wall.id));
  const collinearTolerancePx = options.collinearTolerancePx ?? 1;

  for (let i = 0; i < candidates.length; i += 1) {
    if (usedWallIds.has(candidates[i].wall.id)) continue;
    for (let j = i + 1; j < candidates.length; j += 1) {
      if (usedWallIds.has(candidates[j].wall.id)) continue;
      const a = candidates[i];
      const b = candidates[j];
      if (!areOppositeCollinearThroughPoint(point, a.otherPoint, b.otherPoint, collinearTolerancePx)) {
        continue;
      }

      const healedWallId = nextUniqueDesignerId('w', existingWallIds);
      existingWallIds.add(healedWallId);
      healedWalls.push({
        ...a.wall,
        id: healedWallId,
        startPointId: a.otherPointId,
        endPointId: b.otherPointId,
      });
      openingWallMap.set(a.wall.id, {
        healedWallId,
        oldStart: pointMap.get(a.wall.startPointId),
        oldEnd: pointMap.get(a.wall.endPointId),
        newStart: a.otherPoint,
        newEnd: b.otherPoint,
      });
      openingWallMap.set(b.wall.id, {
        healedWallId,
        oldStart: pointMap.get(b.wall.startPointId),
        oldEnd: pointMap.get(b.wall.endPointId),
        newStart: a.otherPoint,
        newEnd: b.otherPoint,
      });
      usedWallIds.add(a.wall.id);
      usedWallIds.add(b.wall.id);
      break;
    }
  }

  const nextOpenings = state.openings
    .map((opening) => {
      if (!connectedWallIds.has(opening.wallId)) return opening;
      const remap = openingWallMap.get(opening.wallId);
      if (!remap || !remap.oldStart || !remap.oldEnd) return null;
      const oldCenter = pointOnWallAtRatio(remap.oldStart, remap.oldEnd, opening.centerRatio);
      return {
        ...opening,
        wallId: remap.healedWallId,
        centerRatio: projectRatioOnWall(remap.newStart, remap.newEnd, oldCenter),
      };
    })
    .filter(Boolean);
  const nextObjects = (state.objects || [])
    .map((object) => {
      if (object.type === 'furniture' || !connectedWallIds.has(object.wallId)) return object;
      const remap = openingWallMap.get(object.wallId);
      if (!remap || !remap.oldStart || !remap.oldEnd) return null;
      const oldCenter = pointOnWallAtRatio(remap.oldStart, remap.oldEnd, object.positionOnWall);
      return {
        ...object,
        wallId: remap.healedWallId,
        positionOnWall: projectRatioOnWall(remap.newStart, remap.newEnd, oldCenter),
      };
    })
    .filter(Boolean);

  return cleanupFloorPlanState({
    ...state,
    points: state.points.filter((item) => item.id !== pointId),
    walls: [
      ...state.walls.filter((wall) => !connectedWallIds.has(wall.id)),
      ...healedWalls,
    ],
    openings: nextOpenings,
    objects: nextObjects,
    activeChainPointIds: state.activeChainPointIds.filter((id) => id !== pointId),
  });
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {string} color
 * @returns {DesignerState}
 */
export function setWallColor(state, wallId, color) {
  const hex = normalizeMaterialConfig({ materialMode: 'color', color, textureId: null }).color;
  const next = setWallMaterial(state, wallId, { materialMode: 'color', color: hex, textureId: null });
  return {
    ...next,
    walls: next.walls.map((w) => (w.id === wallId ? { ...w, color: hex } : w)),
  };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig} material
 * @param {'inside' | 'outside'} [side]
 * @returns {DesignerState}
 */
export function setWallMaterial(state, wallId, material, side = 'inside') {
  const normalized = normalizeMaterialConfig(material);
  const sideKey = side === 'outside' ? 'outsideMaterial' : 'insideMaterial';
  return {
    ...state,
    walls: state.walls.map((w) =>
      w.id === wallId
        ? {
            ...w,
            [sideKey]: normalized,
            ...(side === 'inside' ? { material: normalized } : {}),
          }
        : w,
    ),
  };
}

/**
 * Resize wall by moving its end point along the existing direction; start point stays fixed.
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {number} newLengthMeters
 * @returns {{ state: DesignerState; ok: boolean; error?: string }}
 */
/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {number} thicknessMeters
 * @returns {{ state: DesignerState; ok: boolean; error?: string }}
 */
export function setWallThickness(state, wallId, thicknessMeters) {
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) {
    return { state, ok: false, error: 'Wall not found.' };
  }
  const parsed = Number(thicknessMeters);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { state, ok: false, error: 'Thickness must be greater than 0 m.' };
  }
  return {
    state: {
      ...state,
      walls: state.walls.map((w) =>
        w.id === wallId ? { ...w, thicknessMeters: parsed } : w,
      ),
    },
    ok: true,
  };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {{ lengthMeters?: number; thicknessMeters?: number; visibleIn3D?: boolean }} patch
 * @returns {{ state: DesignerState; ok: boolean; error?: string }}
 */
export function updateWall(state, wallId, patch) {
  let next = state;
  if (patch.thicknessMeters !== undefined) {
    const thicknessResult = setWallThickness(next, wallId, patch.thicknessMeters);
    if (!thicknessResult.ok) return thicknessResult;
    next = thicknessResult.state;
  }
  if (patch.visibleIn3D !== undefined) {
    next = {
      ...next,
      walls: next.walls.map((w) =>
        w.id === wallId ? { ...w, visibleIn3D: patch.visibleIn3D !== false } : w,
      ),
    };
  }
  if (patch.lengthMeters !== undefined) {
    const lengthResult = setWallLength(next, wallId, patch.lengthMeters);
    if (!lengthResult.ok) return lengthResult;
    next = lengthResult.state;
  }
  return { state: next, ok: true };
}

export function setWallLength(state, wallId, newLengthMeters) {
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) {
    return { state, ok: false, error: 'Wall not found.' };
  }

  const startPt = getPointById(state, wall.startPointId);
  const endPt = getPointById(state, wall.endPointId);
  if (!startPt || !endPt) {
    return { state, ok: false, error: 'Wall points are missing.' };
  }

  if (!Number.isFinite(newLengthMeters) || newLengthMeters < MIN_WALL_LENGTH_METERS) {
    return {
      state,
      ok: false,
      error: `Length must be at least ${MIN_WALL_LENGTH_METERS} m.`,
    };
  }

  const dx = endPt.x - startPt.x;
  const dy = endPt.y - startPt.y;
  const currentLenPx = Math.hypot(dx, dy);
  if (currentLenPx < 1e-6) {
    return { state, ok: false, error: 'Wall has zero length.' };
  }

  const ppm = state.scale.pixelsPerMeter;
  const newLengthPx = newLengthMeters * ppm;
  const dirX = dx / currentLenPx;
  const dirY = dy / currentLenPx;
  const nextCoords = [startPt.x + dirX * newLengthPx, startPt.y + dirY * newLengthPx];

  const next = cleanupFloorPlanState(movePoint(state, wall.endPointId, nextCoords));
  return { state: next, ok: true };
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @returns {DesignerState}
 */
export function cycleWallColor(state, wallId) {
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) return state;
  const idx = WALL_COLOR_PRESETS.indexOf(wall.color);
  const nextColor = WALL_COLOR_PRESETS[(idx + 1) % WALL_COLOR_PRESETS.length];
  return setWallColor(state, wallId, nextColor);
}

function normalizeDoorState(value) {
  if (value === 'open' || value === 'closed' || value === 'frame-only') return value;
  return 'closed';
}

function openAngleForDoorState(doorState) {
  return doorState === 'open' ? 90 : 0;
}

function normalizeDoorOpeningFields(opening) {
  if (opening.type !== 'door') return opening;
  const hingeSide =
    opening.hingeSide === 'right' || opening.hingeSide === 'left'
      ? opening.hingeSide
      : opening.swingDirection === 'right'
        ? 'right'
        : 'left';
  const swingSide =
    opening.swingSide === 'outside' || opening.swingSide === 'inside'
      ? opening.swingSide
      : 'inside';
  const doorState = normalizeDoorState(opening.doorState);
  return {
    ...opening,
    hingeSide,
    swingSide,
    swingDirection: hingeSide,
    doorState,
    openAngle: openAngleForDoorState(doorState),
  };
}

function clampCenterRatio(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0.5;
  return Math.max(0, Math.min(1, parsed));
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {number} widthM
 * @param {number} centerRatio
 */
function clampOpeningCenterRatioForWall(state, wallId, widthM, centerRatio) {
  const seg = getWallSegmentsResolved(state).find((item) => item.id === wallId);
  if (!seg) return clampCenterRatio(centerRatio);
  const wallLengthPx = Math.hypot(seg.end[0] - seg.start[0], seg.end[1] - seg.start[1]);
  const wallLengthM = wallLengthPx * state.scale.metersPerPixel;
  const t = clampCenterRatio(centerRatio);
  if (wallLengthM <= 0) return t;
  const halfRatio = Math.min(0.5, widthM / 2 / wallLengthM);
  return Math.max(halfRatio, Math.min(1 - halfRatio, t));
}

/**
 * @param {DesignerOpening} opening
 * @returns {Record<string, unknown>}
 */
export function openingRecordToSceneObject(opening) {
  const thickness = opening.thicknessMeters ?? (
    opening.type === 'door' ? DEFAULT_DOOR.thicknessMeters : DEFAULT_WINDOW.thicknessMeters
  );
  return {
    id: opening.id,
    type: opening.type,
    wallId: opening.wallId,
    positionOnWall: opening.centerRatio,
    centerRatio: opening.centerRatio,
    width: opening.widthMeters,
    height: opening.heightMeters,
    sillHeight: opening.type === 'window' ? opening.sillHeightMeters : 0,
    thickness,
    thickness2D: thickness,
    frameDepth: opening.type === 'window' ? DEFAULT_WINDOW.frameDepthMeters : thickness,
    swingDirection: opening.swingDirection ?? DEFAULT_DOOR.swingDirection,
    hingeSide: opening.hingeSide ?? opening.swingDirection ?? DEFAULT_DOOR.hingeSide,
    swingSide: opening.swingSide ?? DEFAULT_DOOR.swingSide,
    openAngle: opening.openAngle ?? DEFAULT_DOOR.openAngle,
  };
}

/**
 * @param {DesignerState} state
 * @returns {DesignerState}
 */
export function normalizeDesignerOpenings(state) {
  /** @type {Map<string, DesignerOpening>} */
  const openingById = new Map();

  (state.openings || []).forEach((opening) => {
    if (opening.type !== 'door' && opening.type !== 'window') return;
    openingById.set(opening.id, normalizeDoorOpeningFields({ ...opening }));
  });

  (state.objects || []).forEach((object) => {
    if (object.type !== 'door' && object.type !== 'window') return;
    const existing = openingById.get(object.id);
    const centerRatio = clampCenterRatio(
      existing?.centerRatio ?? object.positionOnWall,
    );
    const merged = {
      id: object.id,
      type: object.type,
      wallId: existing?.wallId ?? object.wallId,
      centerRatio,
      widthMeters: existing?.widthMeters ?? object.width,
      heightMeters: existing?.heightMeters ?? object.height,
      sillHeightMeters:
        existing?.sillHeightMeters ??
        (object.type === 'window' ? object.sillHeight : 0),
      thicknessMeters:
        existing?.thicknessMeters ??
        object.thickness2D ??
        object.thickness ??
        (object.type === 'door' ? DEFAULT_DOOR.thicknessMeters : DEFAULT_WINDOW.thicknessMeters),
      swingDirection: existing?.swingDirection ?? object.swingDirection,
      hingeSide: existing?.hingeSide ?? object.hingeSide,
      swingSide: existing?.swingSide ?? object.swingSide,
      openAngle: existing?.openAngle ?? object.openAngle,
    };
    openingById.set(object.id, normalizeDoorOpeningFields(merged));
  });

  return {
    ...state,
    openings: Array.from(openingById.values()),
    objects: (state.objects || []).filter((object) => object.type === 'furniture'),
  };
}

/**
 * @param {DesignerState} state
 * @param {string} id
 * @returns {DesignerOpening | null}
 */
export function getOpeningById(state, id) {
  const opening = state.openings.find((item) => item.id === id);
  return opening && (opening.type === 'door' || opening.type === 'window')
    ? opening
    : null;
}

/**
 * @param {DesignerState} state
 * @param {{ thicknessMeters?: number; thicknessDirection?: 'inside' | 'outside' }} patch
 * @returns {DesignerState}
 */
export function setWallSettings(state, patch) {
  const parsedThickness =
    patch.thicknessMeters !== undefined
      ? Math.max(0.05, Math.min(0.5, Number(patch.thicknessMeters) || state.wallSettings.thicknessMeters))
      : state.wallSettings.thicknessMeters;
  const thicknessDirection = patch.thicknessDirection ?? state.wallSettings.thicknessDirection;
  const next = {
    ...state,
    wallSettings: {
      thicknessMeters: parsedThickness,
      thicknessDirection,
    },
  };
  if (patch.thicknessMeters !== undefined) {
    return {
      ...next,
      walls: next.walls.map((wall) => ({ ...wall, thicknessMeters: parsedThickness })),
    };
  }
  return next;
}

/**
 * @param {DesignerState} state
 * @param {'door' | 'window'} type
 * @param {string} wallId
 * @param {number} centerRatio
 * @param {{ widthMeters?: number; heightMeters?: number; sillHeightMeters?: number }} [dimensionOverrides]
 * @returns {DesignerState}
 */
export function addOpeningOnWall(state, type, wallId, centerRatio, dimensionOverrides = {}) {
  const defaults = type === 'door' ? DEFAULT_DOOR : DEFAULT_WINDOW;
  const existingIds = new Set([
    ...state.openings.map((item) => item.id),
    ...(state.objects || []).map((item) => item.id),
  ]);
  const id = nextUniqueDesignerId(type, existingIds);
  /** @type {DesignerOpening} */
  const opening = {
    id,
    type,
    wallId,
    centerRatio: clampCenterRatio(centerRatio),
    widthMeters: dimensionOverrides.widthMeters ?? defaults.widthMeters,
    heightMeters: dimensionOverrides.heightMeters ?? defaults.heightMeters,
    sillHeightMeters: dimensionOverrides.sillHeightMeters ?? defaults.sillHeightMeters,
    thicknessMeters: defaults.thicknessMeters,
    ...(type === 'door'
      ? {
          hingeSide: DEFAULT_DOOR.hingeSide,
          swingSide: DEFAULT_DOOR.swingSide,
          swingDirection: DEFAULT_DOOR.swingDirection,
          doorState: DEFAULT_DOOR.doorState,
          openAngle: DEFAULT_DOOR.openAngle,
        }
      : {}),
  };
  return { ...state, openings: [...state.openings, opening] };
}

/**
 * @param {DesignerState} state
 * @param {string} openingId
 * @param {Partial<DesignerOpening>} patch
 * @returns {DesignerState}
 */
export function updateOpening(state, openingId, patch) {
  let updated = false;
  const openings = state.openings.map((opening) => {
    if (opening.id !== openingId) return opening;
    updated = true;
    const next = { ...opening, ...patch };
    if (patch.wallId !== undefined && patch.wallId !== opening.wallId) {
      const widthM =
        Number(next.widthMeters) ||
        (next.type === 'door' ? DEFAULT_DOOR.widthMeters : DEFAULT_WINDOW.widthMeters);
      next.centerRatio = clampOpeningCenterRatioForWall(
        state,
        patch.wallId,
        widthM,
        patch.centerRatio ?? next.centerRatio,
      );
    }
    if (patch.centerRatio !== undefined) {
      const widthM =
        Number(next.widthMeters) ||
        (next.type === 'door' ? DEFAULT_DOOR.widthMeters : DEFAULT_WINDOW.widthMeters);
      next.centerRatio = clampOpeningCenterRatioForWall(
        state,
        patch.wallId ?? next.wallId,
        widthM,
        patch.centerRatio,
      );
    }
    if (patch.widthMeters !== undefined) {
      next.widthMeters = patch.widthMeters;
    }
    if (patch.hingeSide !== undefined) {
      next.hingeSide = patch.hingeSide === 'right' ? 'right' : 'left';
      next.swingDirection = next.hingeSide;
    }
    if (patch.swingSide !== undefined) {
      next.swingSide = patch.swingSide === 'outside' ? 'outside' : 'inside';
    }
    if (patch.swingDirection !== undefined && patch.hingeSide === undefined) {
      next.swingDirection = patch.swingDirection === 'right' ? 'right' : 'left';
      next.hingeSide = next.swingDirection;
    }
    return normalizeDoorOpeningFields(next);
  });
  return updated ? { ...state, openings } : state;
}

/**
 * @param {DesignerState} state
 * @param {string} openingId
 * @returns {DesignerState}
 */
export function removeOpening(state, openingId) {
  return {
    ...state,
    openings: state.openings.filter((opening) => opening.id !== openingId),
    objects: (state.objects || []).filter((object) => object.id !== openingId),
  };
}

function objectOpeningFields(object) {
  if (object.type === 'door') {
    return {
      type: 'door',
      wallId: object.wallId,
      centerRatio: object.positionOnWall,
      widthMeters: object.width,
      heightMeters: object.height,
      sillHeightMeters: 0,
    };
  }
  if (object.type === 'window') {
    return {
      type: 'window',
      wallId: object.wallId,
      centerRatio: object.positionOnWall,
      widthMeters: object.width,
      heightMeters: object.height,
      sillHeightMeters: object.sillHeight,
    };
  }
  return null;
}

function syncOpeningForObject(state, object) {
  const openingFields = objectOpeningFields(object);
  if (!openingFields) return state;
  const opening = { id: object.id, ...openingFields };
  const exists = state.openings.some((item) => item.id === object.id);
  return {
    ...state,
    openings: exists
      ? state.openings.map((item) => (item.id === object.id ? opening : item))
      : [...state.openings, opening],
  };
}

/**
 * @param {DesignerState} state
 * @param {DesignerObject} object
 * @returns {DesignerState}
 */
export function addSceneObject(state, object) {
  const existingIds = new Set((state.objects || []).map((item) => item.id));
  const objectWithId = object.id
    ? object
    : { ...object, id: nextUniqueDesignerId('obj', existingIds) };
  const withObject = {
    ...state,
    objects: [...(state.objects || []), objectWithId],
  };
  return syncOpeningForObject(withObject, objectWithId);
}

/**
 * @param {DesignerState} state
 * @param {string} objectId
 * @returns {DesignerState}
 */
export function removeSceneObject(state, objectId) {
  return {
    ...state,
    objects: (state.objects || []).filter((object) => object.id !== objectId),
    openings: state.openings.filter((opening) => opening.id !== objectId),
  };
}

/**
 * @param {DesignerState} state
 * @param {string} objectId
 * @param {Partial<DesignerObject>} patch
 * @returns {DesignerState}
 */
export function updateSceneObject(state, objectId, patch) {
  let updatedObject = null;
  const nextObjects = (state.objects || []).map((object) => {
    if (object.id !== objectId) return object;
    updatedObject = { ...object, ...patch };
    return updatedObject;
  });
  const next = { ...state, objects: nextObjects };
  return updatedObject ? syncOpeningForObject(next, updatedObject) : next;
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {number} positionOnWall
 * @returns {DesignerState}
 */
export function addDoorObject(state, wallId, positionOnWall, dimensionOverrides = {}) {
  return addOpeningOnWall(state, 'door', wallId, positionOnWall, dimensionOverrides);
}

/**
 * @param {DesignerState} state
 * @param {string} wallId
 * @param {number} positionOnWall
 * @returns {DesignerState}
 */
export function addWindowObject(state, wallId, positionOnWall, dimensionOverrides = {}) {
  return addOpeningOnWall(state, 'window', wallId, positionOnWall, dimensionOverrides);
}

/**
 * @param {DesignerState} state
 * @param {'sofa' | 'bed' | 'table' | 'chair' | 'cabinet'} category
 * @param {{ x: number; z: number; rotationY?: number }} position
 * @returns {DesignerState}
 */
export function addFurnitureObject(state, category, position) {
  const dims = DEFAULT_FURNITURE_DIMENSIONS[category] || DEFAULT_FURNITURE_DIMENSIONS.table;
  const id = nextUniqueDesignerId('furniture', new Set((state.objects || []).map((item) => item.id)));
  return addSceneObject(state, {
    id,
    type: 'furniture',
    category,
    x: position.x,
    z: position.z,
    rotationY: position.rotationY || 0,
    width: dims.width,
    depth: dims.depth,
    height: dims.height,
  });
}

/**
 * @param {DesignerState} state
 * @param {'door' | 'window'} type
 * @param {string} wallId
 * @param {number} centerRatio
 * @returns {DesignerState}
 */
export function addOpening(state, type, wallId, centerRatio) {
  return addOpeningOnWall(state, type, wallId, centerRatio);
}

/**
 * @param {DesignerState} state
 * @returns {DesignerState}
 */
export function clearActiveChain(state) {
  return { ...state, activeChainPointIds: [], draftPreviewPoint: null };
}

/**
 * @param {DesignerState} state
 * @returns {number}
 */
export function countConnectedPoints(state) {
  const ids = new Set();
  state.walls.forEach((w) => {
    ids.add(w.startPointId);
    ids.add(w.endPointId);
  });
  return ids.size;
}
